import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Avatar,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Menu,
  MenuItem
} from '@mui/material';
import { 
  Settings as SettingsIcon,
  Menu as MenuIcon,
  Logout as LogoutIcon,
  Collections as CollectionsIcon,
  Analytics as AnalyticsIcon,
  Favorite as WishlistIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from '../../hooks/useTranslation';

function Navbar() {
  const { currentUser, logout } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);

  async function handleLogout() {
    try {
      await logout();
      setDrawerOpen(false);
      navigate('/');
    } catch (error) {
      console.error('Failed to log out:', error);
    }
  }

  function handleDrawerToggle() {
    setDrawerOpen(!drawerOpen);
  }

  function handleNavigation(path) {
    setDrawerOpen(false);
    navigate(path);
  }

  function handleMenuClick(event) {
    setAnchorEl(event.currentTarget);
  }

  function handleMenuClose() {
    setAnchorEl(null);
  }

  function handleMenuNavigation(path) {
    handleMenuClose();
    navigate(path);
  }

  if (!currentUser) {
    return null; // Don't show navbar for non-authenticated users
  }

  return (
    <>
      <AppBar position="static">
        <Toolbar>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexGrow: 1 }}>
            <img 
              src="/logo.svg" 
              alt="BookPort Logo" 
              style={{ 
                width: '40px', 
                height: '40px',
                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
              }} 
            />
            <Typography 
              variant="h5" 
              component="div" 
              sx={{ 
                fontWeight: 700,
                background: 'linear-gradient(45deg, #8B7355 30%, #D2B48C 90%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              <Link to="/books" style={{ textDecoration: 'none', color: 'inherit' }}>
                BookPort
              </Link>
            </Typography>
          </Box>
          
          {/* Desktop Navigation */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 2 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<CollectionsIcon />}
              component={Link}
              to="/books"
              sx={{
                borderRadius: 20,
                px: 3,
                py: 1,
                textTransform: 'none',
                fontWeight: 600,
              }}
            >
              {t('nav.collection')}
            </Button>
            
            <Button
              variant="outlined"
              color="primary"
              startIcon={<AnalyticsIcon />}
              component={Link}
              to="/insights"
              sx={{
                borderRadius: 20,
                px: 3,
                py: 1,
                textTransform: 'none',
                fontWeight: 600,
                borderColor: 'primary.main',
                color: 'primary.main',
                '&:hover': {
                  borderColor: 'primary.dark',
                  backgroundColor: 'rgba(139, 115, 85, 0.04)',
                },
              }}
            >
              {t('nav.insights')}
            </Button>
            
            <Button
              variant="outlined"
              color="primary"
              startIcon={<WishlistIcon />}
              component={Link}
              to="/wishlist"
              sx={{
                borderRadius: 20,
                px: 3,
                py: 1,
                textTransform: 'none',
                fontWeight: 600,
                borderColor: 'primary.main',
                color: 'primary.main',
                '&:hover': {
                  borderColor: 'primary.dark',
                  backgroundColor: 'rgba(139, 115, 85, 0.04)',
                },
              }}
            >
              {t('nav.wishlist')}
            </Button>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <IconButton
                onClick={handleMenuClick}
                sx={{ p: 0 }}
              >
                <Avatar 
                  sx={{ 
                    width: 36, 
                    height: 36, 
                    bgcolor: 'primary.main',
                    fontWeight: 600,
                    fontSize: '1rem',
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: 'primary.dark',
                    }
                  }}
                >
                  {currentUser.displayName ? currentUser.displayName[0].toUpperCase() : 'U'}
                </Avatar>
              </IconButton>
            </Box>
          </Box>

          {/* Mobile Hamburger Menu */}
          <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="end"
              onClick={handleDrawerToggle}
            >
              <MenuIcon />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* User Menu */}
      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 200,
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          }
        }}
      >
        <MenuItem onClick={() => handleMenuNavigation('/settings')}>
          <ListItemIcon>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          {t('nav.settings')}
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          {t('nav.logout')}
        </MenuItem>
      </Menu>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better mobile performance
        }}
      >
        <Box sx={{ width: 250, pt: 2 }}>
          {/* User Info */}
          <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ width: 40, height: 40, bgcolor: 'secondary.main' }}>
              {currentUser.displayName ? currentUser.displayName[0].toUpperCase() : 'U'}
            </Avatar>
            <Box>
              <Typography variant="subtitle1" noWrap>
                {currentUser.displayName || 'User'}
              </Typography>
              <Typography variant="body2" color="text.secondary" noWrap>
                {currentUser.email}
              </Typography>
            </Box>
          </Box>
          
          <Divider />
          
          {/* Navigation Menu */}
          <List>
            <ListItem button onClick={() => handleNavigation('/books')}>
              <ListItemIcon>
                <CollectionsIcon />
              </ListItemIcon>
              <ListItemText primary={t('collection.title')} />
            </ListItem>
            
            <ListItem button onClick={() => handleNavigation('/insights')}>
              <ListItemIcon>
                <AnalyticsIcon />
              </ListItemIcon>
              <ListItemText primary={t('nav.insights')} />
            </ListItem>
            
            <ListItem button onClick={() => handleNavigation('/wishlist')}>
              <ListItemIcon>
                <WishlistIcon />
              </ListItemIcon>
              <ListItemText primary={t('nav.wishlist')} />
            </ListItem>
            
            <ListItem button onClick={() => handleNavigation('/settings')}>
              <ListItemIcon>
                <SettingsIcon />
              </ListItemIcon>
              <ListItemText primary={t('nav.settings')} />
            </ListItem>
          </List>
          
          <Divider />
          
          {/* Logout */}
          <List>
            <ListItem button onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon />
              </ListItemIcon>
              <ListItemText primary="Logout" />
            </ListItem>
          </List>
        </Box>
      </Drawer>
    </>
  );
}

export default Navbar; 